export class QualipoolDtaVkaMapper {
  /**
   * @typedef MapperResult
   * @type {object[]}
   * @property {string} [].propertyId
   * @property {string} [].propertyAddress
   * @property {string} [].billingPeriodStart
   * @property {string} [].billingPeriodEnd
   * @property {string} [].allocations[].provider.id
   * @property {string} [].allocations[].provider.title
   * @property {string} [].allocations[].consumer.id
   * @property {string} [].allocations[].consumer.title
   * @property {string} [].allocations[].proportion
   * @property {string} [].costDistribution[].unit.id
   * @property {string} [].costDistribution[].occupancy.occupant
   * @property {string} [].costDistribution[].occupancy.periodStart
   * @property {string} [].costDistribution[].occupancy.periodEnd
   * @property {string} [].costDistribution[].consumption.id
   * @property {string} [].costDistribution[].consumption.title
   * @property {string} [].costDistribution[].consumption.type
   * @property {string} [].costDistribution[].consumption.proportion
   */

  /**
   * @param {object} object
   * @returns array|MapperResult
   */
  static apply(object) {
    const instance = new this();
    return instance.map(object);
  }

  /**
   * @param object
   * @returns array|MapperResult
   */
  map(object) {
    return this.mapProperty(object);
  }

  /**
   * @param object
   * @returns array|MapperResult
   */
  mapProperty(object) {
    let result = [];
    for (const costAccounting of object?.["VkaDocument"]?.[0]?.[
      "CostAccounting"
    ]) {
      result.push(this.mapCostAccounting(costAccounting));
    }
    return result;
  }

  mapCostAccounting(costAccounting) {
    let result = {
      propertyId: costAccounting["Id"],
      propertyAddress: costAccounting["Description"],
      billingPeriodStart: costAccounting["PeriodStart"],
      billingPeriodEnd: costAccounting["PeriodEnd"],
      allocations: [],
      costDistribution: []
    };

    for (const costCenter of costAccounting?.["CostCenter"]) {
      result.allocations.push(...this.mapAllocations(costCenter));
      result.costDistribution.push(...this.mapCostDistribution(costCenter));
    }

    return result;
  }

  mapAllocations(costCenter) {
    let result = [];

    for (const allocation of costCenter?.["Allocation"]) {
      result.push({
        provider: {
          id: costCenter.Id,
          title: costCenter.Description
        },
        consumer: {
          id: allocation.CostType,
          title: allocation.Description
        },
        proportion: allocation.Percentage * 10
      });
    }

    return result;
  }

  mapCostDistribution(costCenter) {
    let result = [];

    for (const allocation of costCenter?.["Allocation"] ?? []) {
      for (const costUnit of allocation?.["CostUnit"] ?? []) {
        for (const contract of costUnit?.["Contract"] ?? []) {
          result.push({
            type: allocation.Description,
            unit: costUnit?.["Id"],
            occupant: contract?.["OccupantName"],
            occupancyPeriodStart: contract?.["PeriodStart"],
            occupancyPeriodEnd: contract?.["PeriodEnd"],
            proportion: contract?.["Costs"]?.[0]?.["Value"]?.[0]?.["#text"]
          });
        }
      }
    }

    return result
      .sort((a, b) =>
        a.occupancyPeriodStart.localeCompare(b.occupancyPeriodStart)
      )
      .sort((a, b) => a.unit.localeCompare(b.unit))
      .sort((a, b) => a.type.localeCompare(b.type));
  }

  getTypeById(id) {
    switch (id) {
      case 1:
        return "Heating";
      case 2:
        return "HotWater";
      case 3:
        return "Ventilation";
      case 4:
        return "UtilityCosts";
      case 5:
        return "Water";
      case 6:
        return "Electricity";
      case 7:
        return "Cooling";
      case 8:
        return "OccupantChange";
      case 9:
        return "Heating/HotWater";
      case 10:
        return "Sewage";
      default:
        return "Unknown";
    }
  }
}
